import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Search, MessageCircle, ArrowRight, ShieldCheck, X } from "lucide-react";
import Seo from "../components/Seo";
import { publicApi } from "../lib/api";
import { AnswerRow } from "../components/askcassie/AnswerRow";
import CassieVideoIntro from "../components/askcassie/CassieVideoIntro";
import { AdviceNotice, SearchPrivacyNote } from "../components/askcassie/AdviceNotice";
import { searchAnswers } from "../lib/askSearch";
import { trackAsk, trackAskView } from "../lib/askTrack";
import { trackEvent } from "../lib/analytics";
import cassieAvatar from "../assets/cassie-avatar.webp";

const askCassie = (question) =>
  window.dispatchEvent(new CustomEvent("cass:ask-cassie", { detail: { question } }));

export default function AskCassie() {
  const [data, setData] = useState(null);
  const [term, setTerm] = useState("");
  const recorded = useRef(new Set());
  const counted = useRef(new Set());

  useEffect(() => {
    publicApi.askCassieHub().then(setData).catch(() => setData({ answers: [], categories: [] }));
    trackAskView("hub");
    trackEvent("ask_cassie_opened", { page_path: "/ask-cassie", opened_from: "answer_library" });
  }, []);

  const answers = data?.answers || [];
  // Token-based, synonym-aware and scored (audit T01): "vat threshold" has to
  // find "What is the current VAT registration threshold?".
  const shown = useMemo(() => (term.trim() ? searchAnswers(answers, term) : []), [answers, term]);
  const noMatch = term.trim().length > 2 && shown.length === 0;

  // A search that finds nothing is the most useful thing this page produces:
  // it tells CASS what to write next. The term only — nothing about the person.
  useEffect(() => {
    if (!noMatch) return;
    const t = term.trim().toLowerCase();
    const timer = setTimeout(() => {
      if (recorded.current.has(t)) return;
      recorded.current.add(t);
      publicApi.askCassieUnanswered(term.trim()).catch(() => {});
    }, 1500);
    return () => clearTimeout(timer);
  }, [noMatch, term]);

  // Every search is counted once per visit, with how many answers it returned,
  // so the admin sees the popular questions as well as the dead ends.
  useEffect(() => {
    const t = term.trim();
    if (t.length < 3) return;
    const timer = setTimeout(() => {
      const key = t.toLowerCase();
      if (counted.current.has(key)) return;
      counted.current.add(key);
      trackAsk("search", { page: "hub", term: t, results: shown.length });
    }, 1200);
    return () => clearTimeout(timer);
  }, [term, shown.length]);

  const reviewer = data?.reviewer || {};

  return (
    <div className="bg-white">
      <Seo
        title="Ask Cassie"
        description={data?.intro || "Straight answers to the financial questions business owners actually ask."}
        noindex={!data?.hub_live}
      />

      <section className="bg-navy text-white">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 pt-16 pb-14 md:pt-24 md:pb-20">
          <div className="flex items-center gap-3">
            <img src={cassieAvatar} alt="Cassie" className="w-11 h-11 rounded-full object-cover ring-2 ring-orange/60" />
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-orange">The CASS Knowledge Centre</span>
          </div>
          <h1 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl mt-6 leading-[1.05]">Ask Cassie</h1>
          <p className="text-slate-300 text-base md:text-lg mt-4 max-w-2xl leading-relaxed">
            {data?.intro || "Straight answers to the financial questions business owners actually ask."}
          </p>

          {data?.video_intro && <CassieVideoIntro intro={data.video_intro} />}

          <div className="mt-9 max-w-2xl">
            <label className="relative block">
              <span className="block text-xs font-bold uppercase tracking-wider text-slate-300 mb-2" id="ask-search-label">
                Search the answer library
              </span>
              <Search size={19} className="absolute left-5 top-[calc(50%+0.7rem)] -translate-y-1/2 text-slate-400" />
              <input
                type="search"
                value={term}
                onChange={(e) => setTerm(e.target.value)}
                placeholder="Ask a question — try “vat threshold”"
                aria-labelledby="ask-search-label"
                aria-describedby="ask-search-count"
                data-testid="ask-cassie-input"
                className="w-full rounded-full bg-white text-navy pl-14 pr-14 py-4 text-base placeholder:text-slate-400 focus:outline-none focus:ring-4 focus:ring-orange/40"
              />
              {term && (
                <button
                  type="button"
                  onClick={() => setTerm("")}
                  aria-label="Clear the search"
                  data-testid="ask-cassie-clear"
                  className="absolute right-4 top-[calc(50%+0.7rem)] -translate-y-1/2 w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 text-navy flex items-center justify-center"
                >
                  <X size={15} />
                </button>
              )}
            </label>

            <p id="ask-search-count" role="status" aria-live="polite" className="text-xs text-slate-300 mt-2" data-testid="ask-cassie-result-count">
              {term.trim()
                ? `${shown.length} ${shown.length === 1 ? "answer" : "answers"} found${shown.length ? "" : " — try fewer words"}`
                : `${answers.length} answers in the library`}
            </p>

            {term && shown.length > 0 && (
              <div className="mt-4 rounded-2xl bg-white p-5 max-h-[22rem] overflow-y-auto" data-testid="ask-cassie-results">
                {shown.map((a) => (
                  <AnswerRow key={a.slug} a={a} onOpen={(slug) => trackAsk("open", { page: "hub", slug, term: term.trim() })} />
                ))}
              </div>
            )}

            {noMatch && (
              <div className="mt-4 rounded-2xl bg-white/10 p-5" data-testid="ask-cassie-nomatch">
                <p className="text-sm text-slate-200 leading-relaxed">
                  We couldn't find a close match. Try fewer words, or continue with Cassie — your wording goes across
                  with you, and we note the question so we know what to write next.
                </p>
                <button
                  type="button"
                  onClick={() => { trackAsk("outbound", { page: "hub", target: "cassie-handoff", term: term.trim() }); askCassie(term); }}
                  data-testid="ask-cassie-handoff"
                  className="mt-4 inline-flex items-center gap-2 bg-orange hover:bg-orange-dark text-white font-bold text-sm px-5 py-2.5 rounded-full transition-colors"
                >
                  <MessageCircle size={15} /> Continue with Cassie
                </button>
              </div>
            )}
            <AdviceNotice dark className="mt-6" />
            <SearchPrivacyNote dark className="mt-3" />
          </div>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-5 sm:px-8 py-14 md:py-20">
        {(data?.featured || []).length > 0 && (
          <>
            <h2 className="text-base md:text-lg font-display font-bold text-navy">Most asked</h2>
            <div className="mt-2" data-testid="ask-most-asked">
              {data.featured.map((a) => (
                <AnswerRow key={a.slug} a={a} onOpen={(slug) => trackAsk("open", { page: "hub", slug })} />
              ))}
            </div>
          </>
        )}

        <h2 className="text-base md:text-lg font-display font-bold text-navy mt-14">Browse the library</h2>
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mt-5">
          {/* Once the library is live, a subject with nothing approved in it is
              an empty room: it stays out of the list until it has an answer. */}
          {(data?.categories || []).filter((c) => !data?.hub_live || c.count > 0).map((c) => (
            <Link
              key={c.key}
              to={c.url || `/ask-cassie/${c.key}`}
              data-testid={`ask-category-${c.key}`}
              className="group rounded-2xl border border-slate-200 hover:border-orange p-5 transition-all hover:-translate-y-0.5"
            >
              <span className="font-display font-bold text-navy text-sm block group-hover:text-orange transition-colors">
                {c.name}
              </span>
              <span className="text-xs text-slate-500 leading-relaxed block mt-1.5">{c.blurb}</span>
              <span className="text-[11px] font-bold uppercase tracking-wide text-slate-400 block mt-3">
                {c.count} question{c.count === 1 ? "" : "s"}
              </span>
            </Link>
          ))}
        </div>

        {(data?.recent || []).length > 0 && (
          <div className="mt-14" data-testid="ask-recently-reviewed">
            <h2 className="text-base md:text-lg font-display font-bold text-navy">Recently reviewed</h2>
            <div className="mt-2">
              {data.recent.map((a) => (
                <AnswerRow key={a.slug} a={a} onOpen={(slug) => trackAsk("open", { page: "hub", slug })} />
              ))}
            </div>
          </div>
        )}

        {reviewer.url && (
          <p className="mt-14 flex flex-wrap items-center gap-2 text-xs text-slate-500">
            <ShieldCheck size={14} className="text-teal" /> Every answer is written and reviewed by the CASS team.
            <Link to={reviewer.url} data-testid="ask-reviewer-link" className="inline-flex items-center gap-1 font-bold text-navy hover:text-orange transition-colors">
              Who reviews them <ArrowRight size={12} />
            </Link>
          </p>
        )}
      </section>
    </div>
  );
}
